import {objectFromEntries} from '@benchling/js-utils';
import React from 'react';

import {DataFrameSpec} from 'data-frames/DataFrameTypes';
import columnToTypeIcon from 'data-frames/util/columnToTypeIcon';
import getUserColumns from 'data-frames/util/getUserColumns';
import {ColumnTypeValidationOptions, isValidColumnType} from 'data-frames/util/validateColumnType';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import {ButtonSize} from 'ui-library/buttons/Button';
import Chip, {ChipSize} from 'ui-library/chips/Chip';
import SelectDropdown, {CornerRadius, SelectDropdownErrorStyle} from 'ui-library/dropdowns/SelectDropdown';
import {ToggleDisplayMode} from 'ui-library/floating-ui/Float';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import {MenuHeight, MenuWidth, OptionData} from 'ui-library/menus/MenuConstants';
import SearchableMenu from 'ui-library/menus/SearchableMenu';

export interface ColumnDropdownSelectorProps {
  dataFrameSpec: Pick<DataFrameSpec, 'columns'>;
  /**
   * Optional column validation options. Columns that do not match these options will not be shown in the
   * selector.
   *
   * If not provided, all user-defined columns in the `dataFrameSpec` will be shown.
   */
  validationOptions?: ColumnTypeValidationOptions;

  selectedColumnSystemName: string | null;
  onChange: (newColumnSystemName: string | null) => void;

  /**
   * Text to display when the selected column is invalid. If the selected column does not conform to the
   * provided validationOptions, a "Invalid column selected" error will be displayed regardless of this prop.
   */
  error?: string;
  /**
   * Text to display when no column is selected.
   *  @default "None"
   */
  noColumnSelectedText?: string;
  buttonSize?: ButtonSize;
  buttonClassName?: string;
  disabled?: boolean;
  hideTypeIcon?: boolean;
  cornerRadius?: CornerRadius;

  dataTestAttrs?: DataTestAttrs;
  allowSelectNull?: boolean;
}

/**
 * Component that renders a dropdown selector and enables the user to select 1 column within 1 data frame
 *
 * See also:
 *   * `MultiSelectColumnDropdown` - component that renders a dropdown selector and enables the user to select multiple
 *     columns within 1 data frame
 *     * Path: client/data-frame/column-selector/MultiSelectColumnDropdown.tsx
 */
export default function ColumnDropdownSelector(props: ColumnDropdownSelectorProps): JSX.Element {
  const columns = getUserColumns(props.dataFrameSpec);
  const columnsBySystemName = objectFromEntries(columns.map((c) => [c.systemName, c]));
  const selectedColumn = props.selectedColumnSystemName
    ? columnsBySystemName[props.selectedColumnSystemName]
    : null;

  const validationOptions = props.validationOptions ?? {};

  function valueToDisplayName(value: string | null): string {
    return value != null && columnsBySystemName[value] != null
      ? columnsBySystemName[value].displayName
      : (props.noColumnSelectedText ?? 'None');
  }
  function valueToIcon(value: string | null): BenchlingIconName {
    return value != null ? columnToTypeIcon(columnsBySystemName[value]) : 'none-option';
  }

  const optionValues: Array<string | null> = [
    ...(props.allowSelectNull ? [null] : []),
    ...columns.filter((column) => isValidColumnType(column.type, validationOptions)).map((c) => c.systemName),
  ];
  const optionDatas: Array<OptionData> = optionValues.map((value) => ({
    icon: props.hideTypeIcon ? undefined : valueToIcon(value),
    name: valueToDisplayName(value),
    value,
  }));
  const menu = (
    <SearchableMenu
      height={MenuHeight.MEDIUM}
      width={MenuWidth.FULL}
      selectedValue={props.selectedColumnSystemName}
      onSelect={(newColumnSystemName): void => {
        props.onChange(newColumnSystemName);
      }}
      optionDatas={optionDatas}
    />
  );
  return (
    <SelectDropdown
      buttonSize={props.buttonSize}
      buttonClassName={props.buttonClassName}
      menu={menu}
      displayName={
        selectedColumn == null ? (
          (props.noColumnSelectedText ?? 'None')
        ) : (
          <Chip
            size={ChipSize.SMALL}
            icon={props.hideTypeIcon ? null : valueToIcon(props.selectedColumnSystemName)}
            text={valueToDisplayName(props.selectedColumnSystemName)}
            isReadonly={true}
          />
        )
      }
      error={
        selectedColumn != null && !isValidColumnType(selectedColumn.type, validationOptions)
          ? 'Invalid column selected'
          : props.error
      }
      errorStyle={SelectDropdownErrorStyle.TOOLTIP}
      tooltipToggleDisplayMode={ToggleDisplayMode.BLOCK}
      disabled={props.disabled}
      cornerRadius={props.cornerRadius}
      dataTestAttrs={props.dataTestAttrs}
    />
  );
}
